// last modified by AI tools on 2026-10-06
/**
 * FTAPI-231: Converts the Election Hub "balance of power" featured products
 * (Federal Race Outcomes + the office-specific control product, from
 * useElectionFeaturedProducts) into the same ForecastTraderElectionGridProduct
 * shape the real election/grid endpoint returns. ElectionGrid prepends the
 * result to ElectionTableV2's `products` prop so they render as ordinary
 * top-of-list rows — same columns, same buy/sell/position/open-interest/
 * exchange behavior as every other race. No separate section or header.
 */
import type {
  ForecastTraderDetailedMarket,
  ForecastTraderStrike,
  ForecastTraderElectionGridProduct,
  ForecastTraderElectionGridContract,
  ForecastTraderElectionGridMatchedContract,
} from '@/types/forecasttrader.types';

/**
 * Placeholder state for synthetic balance-of-power "races" — these are
 * national/chamber-level products, not tied to any one state. "District of
 * Columbia" is a real, valid state name so it maps cleanly to a state code
 * with no fallback warning; it's never rendered — the table shows
 * product.name ("Federal Race Outcomes", etc.) in the Race column instead.
 */
const BALANCE_OF_POWER_STATE = 'District of Columbia';

function buildMatchedContracts(
  strike: ForecastTraderStrike,
  market: ForecastTraderDetailedMarket,
): ForecastTraderElectionGridMatchedContract[] {
  if (!strike.yesConidExPairs || !strike.noConidExPairs) return [];
  const matched: ForecastTraderElectionGridMatchedContract[] = [];
  // Index 0 is the representative exchange already used for conid_yes/conid_no — only
  // the remaining pairs (other exchanges) become `matched` entries.
  for (let i = 1; i < strike.yesConidExPairs.length; i++) {
    const [yesConidStr, exchange] = strike.yesConidExPairs[i]?.split('@') ?? [];
    const [noConidStr] = strike.noConidExPairs[i]?.split('@') ?? [];
    if (!exchange || !yesConidStr || !noConidStr) continue;
    const exchangeIdx = market.exchanges?.indexOf(exchange) ?? -1;
    const productConid = exchangeIdx >= 0 ? (market.productConids?.[exchangeIdx] ?? market.conid) : market.conid;
    matched.push({
      conid_yes: Number(yesConidStr),
      conid_no: Number(noConidStr),
      exchange,
      product_conid: productConid,
    });
  }
  return matched;
}

function mapStrikeToGridContract(
  strike: ForecastTraderStrike,
  market: ForecastTraderDetailedMarket,
): ForecastTraderElectionGridContract {
  const matched = buildMatchedContracts(strike, market);
  return {
    conid_yes: strike.conid_yes,
    conid_no: strike.conid_no,
    strike_label: strike.strike_label,
    exchange: strike.exchange ?? market.exchange,
    bid_yes: strike.bid_yes,
    bid_no: strike.bid_no,
    open_interest: strike.open_interest,
    party: strike.party,
    // Required by ForecastTraderElectionGridContract; parseStrikeLabelWithAffiliation
    // falls back to this when strike_label has no "(Party)" suffix of its own.
    party_affiliation: strike.party ?? strike.strike_label,
    ...(matched.length > 0 && { matched }),
  };
}

function mapFeaturedMarketToGridProduct(market: ForecastTraderDetailedMarket): ForecastTraderElectionGridProduct {
  const contracts = market.strikes.map((strike) => mapStrikeToGridContract(strike, market));
  const centerStrike = market.strikes.find((s) => s.is_center) ?? market.strikes[0];
  return {
    conid: market.conid,
    name: market.name,
    state: BALANCE_OF_POWER_STATE,
    leading_candidate_conid: centerStrike?.conid_yes ?? market.conid,
    last_trade_time: centerStrike?.last_trade_time ?? 0,
    // FTAPI-231: these settle on the general election, not a fixed calendar day —
    // show the service-provided period (e.g. "Nov'26") instead of a formatted date.
    measured_period: centerStrike?.measured_period,
    open_interest: market.open_interest,
    strike_count: market.strike_count,
    exchanges: market.exchanges,
    contracts,
  };
}

/**
 * Maps balance-of-power markets (from useElectionFeaturedProducts) into
 * ForecastTraderElectionGridProduct[], ready to prepend to the real election
 * grid's products. Markets with no strikes are dropped — a product with zero
 * contracts can't form a race row.
 */
export function mapFeaturedMarketsToGridProducts(
  markets: ForecastTraderDetailedMarket[],
): ForecastTraderElectionGridProduct[] {
  return markets.filter((market) => market.strikes.length > 0).map(mapFeaturedMarketToGridProduct);
}
